import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { useConfig } from "@/config/context"
import type { FeedInfo } from "@/nostr/types"
import { readStorage, StorageKeys, writeStorage } from "@/utils/storage"
import { normalizeRelayUrl, simplifyUrl, uniqueRelayUrls } from "@/utils/url"
import { useAuth } from "./AuthContext"
import { useRelays } from "./RelayContext"

type StoredFeedMap = Record<string, FeedInfo | null>

interface FeedContextValue {
  feedInfo: FeedInfo | null
  /** Relays read by the active feed. Empty when no feed is selected. */
  feedRelays: string[]
  feedName: string
  isReady: boolean
  switchFeed: (info: FeedInfo | null) => void
  switchToRelaySet: (setId: string) => void
  switchToRelay: (relayUrl: string) => void
  switchToFollowing: () => void
}

const FeedContext = createContext<FeedContextValue | undefined>(undefined)

function feedKey(pubkey?: string): string {
  return pubkey ?? "guest"
}

export function FeedProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const { pubkey } = useAuth()
  const { relaySets, favoriteRelays, appRelays } = useRelays()
  const [storedFeeds, setStoredFeeds] = useState<StoredFeedMap>(() =>
    readStorage<StoredFeedMap>(StorageKeys.feedInfo, {}),
  )
  const [feedInfo, setFeedInfo] = useState<FeedInfo | null>(null)
  const [isReady, setIsReady] = useState(false)

  const resolveRelays = useCallback(
    (info: FeedInfo | null): string[] => {
      if (!info) return []
      if (info.feedType === "relay") {
        const url = normalizeRelayUrl(info.id ?? "")
        return url ? [url] : []
      }
      if (info.feedType === "relays") {
        const set = relaySets.find((item) => item.id === info.id) ?? relaySets[0]
        return set ? uniqueRelayUrls(set.relayUrls) : []
      }
      return appRelays
    },
    [relaySets, appRelays],
  )

  // Pick the initial feed once the relay library is known.
  useEffect(() => {
    const key = feedKey(pubkey)
    const stored = storedFeeds[key]

    if (config.community.mode) {
      const preset = config.community.relaySets[0]
      if (preset) {
        const relays = uniqueRelayUrls(preset.relayUrls)
        if (relays.length > 0) {
          setFeedInfo({ feedType: "relays", id: preset.id })
          setIsReady(true)
          return
        }
      }
      if (config.community.extraRelays.length > 0) {
        setFeedInfo({ feedType: "relay", id: normalizeRelayUrl(config.community.extraRelays[0]) })
        setIsReady(true)
        return
      }
    }

    if (stored) {
      setFeedInfo(stored)
      setIsReady(true)
      return
    }

    if (pubkey) {
      setFeedInfo({ feedType: "following" })
      setIsReady(true)
      return
    }

    if (relaySets.length > 0) {
      setFeedInfo({ feedType: "relays", id: relaySets[0].id })
      setIsReady(true)
      return
    }

    if (favoriteRelays.length > 0) {
      setFeedInfo({ feedType: "relay", id: favoriteRelays[0] })
      setIsReady(true)
      return
    }

    setFeedInfo(null)
    setIsReady(true)
  }, [
    pubkey,
    config.community.mode,
    config.community.relaySets,
    config.community.extraRelays,
    relaySets,
    favoriteRelays,
    storedFeeds,
  ])

  const switchFeed = useCallback(
    (info: FeedInfo | null) => {
      const normalized: FeedInfo | null =
        info && info.feedType === "relay"
          ? { feedType: "relay", id: normalizeRelayUrl(info.id ?? "") }
          : info
      setFeedInfo(normalized)
      const key = feedKey(pubkey)
      setStoredFeeds((current) => {
        const next = { ...current, [key]: normalized }
        writeStorage(StorageKeys.feedInfo, next)
        return next
      })
    },
    [pubkey],
  )

  const feedRelays = useMemo(() => resolveRelays(feedInfo), [resolveRelays, feedInfo])

  const feedName = useMemo(() => {
    if (!feedInfo) return ""
    if (feedInfo.feedType === "following") return "Following"
    if (feedInfo.feedType === "relay") return simplifyUrl(feedInfo.id ?? "")
    return relaySets.find((set) => set.id === feedInfo.id)?.name ?? "Relay set"
  }, [feedInfo, relaySets])

  const value = useMemo<FeedContextValue>(
    () => ({
      feedInfo,
      feedRelays,
      feedName,
      isReady,
      switchFeed,
      switchToRelaySet: (setId) => switchFeed({ feedType: "relays", id: setId }),
      switchToRelay: (relayUrl) => switchFeed({ feedType: "relay", id: relayUrl }),
      switchToFollowing: () => switchFeed({ feedType: "following" }),
    }),
    [feedInfo, feedRelays, feedName, isReady, switchFeed],
  )

  return <FeedContext.Provider value={value}>{children}</FeedContext.Provider>
}

export function useFeed(): FeedContextValue {
  const context = useContext(FeedContext)
  if (!context) throw new Error("useFeed must be used inside a FeedProvider")
  return context
}
